import { type FC, useRef, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { useSimulation } from '@/hooks/useSimulation';
import { WorldMap } from '@/components/map/WorldMap';
import { ImpactMeters } from './ImpactMeters';
import { TurnNarrative } from './TurnNarrative';
import { ChoicePanel } from './ChoicePanel';
import type { ScenarioType } from '@/types/simulation';

const SCENARIO_LABELS: Record<ScenarioType, string> = {
  'direct-attack': 'Direct Military Attack',
  'nuclear-threat': 'Nuclear Threat',
  'naval-blockade': 'Naval Blockade',
  'proxy-war': 'Proxy War',
  'hybrid-warfare': 'Hybrid Warfare',
  'trade-war': 'Trade War / Tariffs',
  'sanctions': 'Economic Sanctions',
  'energy-cutoff': 'Energy Cutoff',
  'tech-ban': 'Tech Export Ban',
  'currency-war': 'Currency War',
  'coup': 'Coup / Regime Change',
  'election-interference': 'Election Interference',
  'territorial-annexation': 'Territorial Annexation',
  'political-assassination': 'Political Assassination',
  'diplomatic-break': 'Diplomatic Break',
  'alliance-formation': 'Alliance Formation',
  'un-crisis': 'UN Security Council Crisis',
  'cyber-attack': 'Cyber Attack',
  'space-weapons': 'Space Weaponization',
  'ai-arms-race': 'AI Arms Race',
  'refugee-crisis': 'Refugee Crisis',
  'resource-war': 'Resource War',
  'climate-conflict': 'Climate Conflict',
};

const TIME_LABELS: Record<string, string> = {
  days: 'Day', weeks: 'Week', months: 'Month', years: 'Year',
};

export const SimulationDashboard: FC<{ onClose: () => void }> = ({ onClose }) => {
  const simulation = useAppStore((s) => s.simulation);
  const setAutoMode = useAppStore((s) => s.setAutoMode);
  const { runTurn, stop } = useSimulation();
  const timelineRef = useRef<HTMLDivElement>(null);

  const { scenario, turns, currentTurn, status, streamingText, autoMode } = simulation;
  const lastTurn = turns[turns.length - 1];
  const isStreaming = status === 'streaming';
  const isWaiting = status === 'waiting';

  // Auto-start first turn — ref guard prevents React StrictMode double-invoke
  const hasStarted = useRef(false);
  useEffect(() => {
    if (turns.length === 0 && status === 'building' && !hasStarted.current) {
      hasStarted.current = true;
      void runTurn(undefined);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll timeline to bottom only when a new turn completes (not on every chunk)
  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollTop = timelineRef.current.scrollHeight;
    }
  }, [turns.length]);

  const handleChoice = (choiceId: 'A' | 'B' | 'C') => {
    if (!isWaiting) return;
    void runTurn(choiceId);
  };

  const handleAutoToggle = () => {
    if (autoMode) {
      stop();
    } else {
      setAutoMode(true);
    }
  };

  if (!scenario) return null;

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-slate-800 bg-gray-950/80 shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <span className="text-[10px] font-bold text-amber-400 uppercase tracking-widest bg-amber-950/40 border border-amber-700/40 px-2 py-0.5 rounded shrink-0">
            🎯 SIM
          </span>
          <span className="text-xs text-slate-300 font-semibold truncate">
            {scenario.initiator}
            <span className="text-slate-600 mx-2">→</span>
            {scenario.target}
          </span>
          <span className="text-[10px] text-slate-600 truncate hidden sm:block">
            {SCENARIO_LABELS[scenario.type]}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] text-slate-500 tabular-nums">
            {TIME_LABELS[scenario.timeHorizon]} {currentTurn}
          </span>

          {/* Auto mode toggle */}
          <button
            onClick={handleAutoToggle}
            className={`text-[10px] font-bold px-2 py-1 rounded border transition-all ${
              autoMode
                ? 'border-amber-500/60 bg-amber-950/40 text-amber-400 animate-pulse'
                : 'border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
            }`}
          >
            {autoMode ? '⏸ AUTO' : '▶ AUTO'}
          </button>

          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-300 text-lg leading-none px-1"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Main content: Map (left) + Dashboard (right) */}
      <div className="flex-1 flex overflow-hidden">
        {/* Map — left 55% */}
        <div className="flex-1 relative overflow-hidden">
          <WorldMap />
          {/* Simulation overlay badge */}
          <div className="absolute top-2 left-2 z-50 pointer-events-none">
            <div className="bg-red-950/90 border border-red-700/60 rounded px-2 py-1 text-[9px] font-bold text-red-400 uppercase tracking-wider animate-pulse">
              ● SIMULATION ACTIVE
            </div>
          </div>
        </div>

        {/* Right panel — 38% */}
        <div className="w-[38%] min-w-[320px] max-w-[480px] flex flex-col border-l border-slate-800 bg-gray-950/95 overflow-hidden">

          {/* Turn title */}
          {(lastTurn || isStreaming) && (
            <div className="px-4 py-2.5 border-b border-slate-800/50 shrink-0">
              <p className="text-[11px] font-bold text-amber-400 uppercase tracking-wide">
                {lastTurn?.turn_title ?? 'Analyzing scenario...'}
              </p>
              {lastTurn?.next_turn_preview && (
                <p className="text-[10px] text-slate-600 mt-0.5 truncate">
                  Next: {lastTurn.next_turn_preview}
                </p>
              )}
            </div>
          )}

          {/* Scrollable content */}
          <div ref={timelineRef} className="flex-1 overflow-y-auto scrollbar-thin px-4 py-3 space-y-4">

            {/* Initial loading state — never show raw JSON */}
            {turns.length === 0 && isStreaming && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs text-amber-400">
                  <span className="animate-spin">⟳</span>
                  <span>Analyzing scenario with intelligence engine...</span>
                </div>
                <div className="space-y-2">
                  <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-full" />
                  <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-4/5" />
                  <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-full" />
                  <div className="h-2.5 bg-slate-800/50 rounded animate-pulse w-2/3" />
                </div>
              </div>
            )}

            {/* Impact Meters — only show after turn completes */}
            {lastTurn && !isStreaming && <ImpactMeters turn={lastTurn} />}
            {isStreaming && (
              <div className="space-y-2">
                {[80, 60, 100, 45].map((w, i) => (
                  <div key={i} className="bg-gray-900/40 border border-slate-800/40 rounded-lg px-3 py-2.5">
                    <div className={`h-2 bg-slate-800/60 rounded animate-pulse w-${w > 75 ? 'full' : w > 55 ? '3/4' : '1/2'}`} />
                  </div>
                ))}
              </div>
            )}

            {/* Narrative */}
            {(lastTurn || isStreaming) && (
              <div className="border-t border-slate-800/50 pt-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Intelligence Report
                </p>
                {isStreaming ? (
                  /* Skeleton while Claude streams — never show raw JSON */
                  <div className="space-y-2">
                    <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-full" />
                    <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-5/6" />
                    <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-full" />
                    <div className="h-2.5 bg-slate-800/60 rounded animate-pulse w-3/4" />
                    <div className="h-2.5 bg-slate-800/50 rounded animate-pulse w-full mt-1" />
                    <div className="h-2.5 bg-slate-800/50 rounded animate-pulse w-4/5" />
                  </div>
                ) : (
                  <TurnNarrative text={lastTurn?.narrative ?? ''} streaming={false} />
                )}
              </div>
            )}

            {/* Events log */}
            {lastTurn && lastTurn.events.length > 0 && (
              <div className="border-t border-slate-800/50 pt-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Events Log
                </p>
                <div className="space-y-1.5">
                  {lastTurn.events.map((ev, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px]">
                      <span className={`shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase mt-0.5 ${
                        ev.type === 'military' ? 'bg-red-950 text-red-400' :
                        ev.type === 'economic' ? 'bg-yellow-950 text-yellow-400' :
                        ev.type === 'diplomatic' ? 'bg-sky-950 text-sky-400' :
                        ev.type === 'humanitarian' ? 'bg-emerald-950 text-emerald-400' :
                        'bg-purple-950 text-purple-400'
                      }`}>
                        {ev.type.slice(0, 3).toUpperCase()}
                      </span>
                      <span className="text-slate-400 leading-snug">{ev.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Turn history pills */}
            {turns.length > 1 && (
              <div className="border-t border-slate-800/50 pt-3">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                  Timeline
                </p>
                <div className="space-y-1">
                  {turns.slice(0, -1).map((t) => (
                    <div key={t.turnNumber} className="flex items-center gap-2 text-[10px] text-slate-600">
                      <span className="tabular-nums text-slate-700 w-6">{t.turnNumber}</span>
                      <span className="truncate">{t.turn_title}</span>
                      {t.chosenId && (
                        <span className="shrink-0 text-amber-700">[{t.chosenId}]</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Choice panel — pinned to bottom */}
          {isWaiting && lastTurn && lastTurn.choices.length > 0 && (
            <div className="px-4 py-3 border-t border-slate-800 bg-gray-950/80 shrink-0">
              <ChoicePanel
                choices={lastTurn.choices}
                onChoose={handleChoice}
                disabled={!isWaiting || autoMode}
                autoMode={autoMode}
              />
            </div>
          )}

          {/* Streaming indicator */}
          {isStreaming && (
            <div className="px-4 py-2 border-t border-amber-900/30 bg-amber-950/10 shrink-0">
              <div className="flex items-center gap-2 text-[10px] text-amber-500">
                <span className="animate-spin">⟳</span>
                <span>Claude is generating the next turn...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
